import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Grid, Button, Card, CardContent } from '@mui/material';
import {
  LibraryBooks as LibraryIcon,
  ListAlt as EntryIcon,
  Timeline as TimelineIcon,
  RateReview as ReviewIcon,
  TrendingUp as TrendingIcon,
  FactCheck as IndexChannelIcon,
  Chat as ChannelsIcon,
  Forum as ChatPreviewIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useLibraries } from '@/hooks/useLibraries';
import { useEntries } from '@/hooks/useEntries';
import { EntryStatus } from '@/types/enums';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { VerifiedEntry } from '@/types/api';
import { formatDate } from '@/utils/dateUtils';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages');

  // Fetch data for statistics
  const { data: libraries, isLoading: librariesLoading } = useLibraries();
  const { data: entriesData, isLoading: entriesLoading } = useEntries({ limit: 1000 });

  const isLoading = librariesLoading || entriesLoading;

  // Calculate statistics
  const totalLibraries = libraries?.length || 0;
  const totalEntries = entriesData?.length || 0;

  const entries = entriesData ?? [];

  const entriesByStatus = entries.reduce(
    (acc: Record<EntryStatus, number>, entry: VerifiedEntry) => {
        acc[entry.status] = (acc[entry.status] || 0) + 1;
        return acc;
      },
    {} as Record<EntryStatus, number>
  ) || {};

  const publishedCount = entriesByStatus[EntryStatus.PUBLISHED] || 0;
  const reviewedCount = entriesByStatus[EntryStatus.REVIEWED] || 0;
  const draftCount = entriesByStatus[EntryStatus.DRAFT] || 0;

  // Recent entries
  const recentEntries = entries.slice(0, 5);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('dashboard.title')}
      </Typography>

      {isLoading && <LoadingSpinner message={t('dashboard.loading')} />}

      {!isLoading && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LibraryIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.stats.libraries')}
                    </Typography>
                  </Box>
                  <Typography variant="h3">{totalLibraries}</Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/libraries')}
                    sx={{ mt: 1 }}
                  >
                    {t('dashboard.stats.manage')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EntryIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.stats.totalEntries')}
                    </Typography>
                  </Box>
                  <Typography variant="h3">{totalEntries}</Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/entries')}
                    sx={{ mt: 1 }}
                  >
                    {t('dashboard.stats.viewAll')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.stats.published')}
                    </Typography>
                  </Box>
                  <Typography variant="h3">{publishedCount}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {totalEntries > 0
                      ? `${((publishedCount / totalEntries) * 100).toFixed(0)}${t('dashboard.stats.ofTotal')}`
                      : t('dashboard.stats.noEntries')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ReviewIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.stats.pendingReview')}
                    </Typography>
                  </Box>
                  <Typography variant="h3">{draftCount + reviewedCount}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.stats.draft', { count: draftCount })}, {t('dashboard.stats.reviewed', { count: reviewedCount })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard.quickActions.title')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ReviewIcon />}
                  onClick={() => navigate('/review')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.reviewSuggestions')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<IndexChannelIcon />}
                  onClick={() => navigate('/index-channel')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.indexChannel')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<TimelineIcon />}
                  onClick={() => navigate('/timeline')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.browseTimeline')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ChannelsIcon />}
                  onClick={() => navigate('/channels')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.viewChannels')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<LibraryIcon />}
                  onClick={() => navigate('/libraries')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.manageLibraries')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<EntryIcon />}
                  onClick={() => navigate('/entries')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.viewEntries')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ChatPreviewIcon />}
                  onClick={() => navigate('/chat-preview')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.chatPreview')}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SettingsIcon />}
                  onClick={() => navigate('/settings')}
                  sx={{ py: 2 }}
                >
                  {t('dashboard.quickActions.settings')}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Recent Entries */}
          {recentEntries.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.recentEntries.title')}
              </Typography>
              <Box>
                {recentEntries.map((entry: VerifiedEntry) => (
                  <Box
                    key={entry.id}
                    sx={{
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography variant="body1" sx={{ direction: 'rtl', textAlign: 'right' }}>
                      {entry.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.author} • {entry.ms_no} • {entry.status} •{' '}
                      {formatDate(entry.created_at, 'PP')}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Button
                size="small"
                onClick={() => navigate('/entries')}
                sx={{ mt: 2 }}
              >
                {t('dashboard.recentEntries.viewAll')}
              </Button>
            </Paper>
          )}

          {/* Getting Started */}
          {totalEntries === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {t('dashboard.gettingStarted.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t('dashboard.gettingStarted.subtitle')}
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                <Grid item>
                  <Button
                    variant="contained"
                    startIcon={<LibraryIcon />}
                    onClick={() => navigate('/libraries')}
                  >
                    {t('dashboard.gettingStarted.createLibrary')}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<ReviewIcon />}
                    onClick={() => navigate('/review')}
                  >
                    {t('dashboard.gettingStarted.startReviewing')}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default DashboardPage;
